// @ts-nocheck
import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
  IconButton,
  Textarea,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { LuPenLine } from "react-icons/lu";

import { type ApiError, type SchemaPublic, SchemaService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";
import { handleError } from "@/utils";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";
import type { SimpleSchemaField } from "@/components/Document/DocumentSchema";

interface EditSchemaProps {
  schema: SchemaPublic;
  currentSchemaContent: SimpleSchemaField[];
  onSuccess?: () => void;
}

interface SchemaUpdateForm {
  schema_name: string;
  description?: string;
}

const EditSchema = ({
  schema,
  currentSchemaContent,
  onSuccess,
}: EditSchemaProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SchemaUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      schema_name: schema.schema_name,
      description: schema.description ?? "",
    },
  });

  // 다이얼로그가 열릴 때마다 폼 초기화
  useEffect(() => {
    if (isOpen) {
      reset({
        schema_name: schema.schema_name,
        description: schema.description ?? "",
      });
    }
  }, [isOpen, schema, reset]);

  const mutation = useMutation({
    mutationFn: (formData: SchemaUpdateForm) => {
      const requestBody = {
        ...formData,
        schema_content: currentSchemaContent,
      };
      const result = SchemaService.updateSchema({
        schemaId: schema.id,
        requestBody,
      });
      return result;
    },
    onSuccess: (data) => {
      showSuccessToast("스키마가 성공적으로 수정되었습니다.");
      reset();
      setIsOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err: ApiError) => {
      showErrorToast("스키마 수정에 실패했습니다.");
      handleError(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["schemas"] });
      queryClient.invalidateQueries({ queryKey: ["schema", schema.id] });
    },
  });

  const onSubmit: SubmitHandler<SchemaUpdateForm> = async (data) => {
    mutation.mutate(data);
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
      }}
    >
      <DialogTrigger asChild>
        <IconButton aria-label="edit schema" variant="ghost" size="sm">
          <LuPenLine color="blue" />
        </IconButton>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>스키마 수정</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>스키마 정보를 수정하세요.</Text>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.schema_name}
                errorText={errors.schema_name?.message}
                label="스키마 이름"
              >
                <Input
                  id="schema_name"
                  {...register("schema_name", {
                    required: "스키마 이름은 필수 입력 항목입니다.",
                  })}
                  placeholder="스키마 이름"
                  type="text"
                />
              </Field>

              <Field
                invalid={!!errors.description}
                errorText={errors.description?.message}
                label="스키마 설명"
              >
                <Textarea
                  id="description"
                  {...register("description")}
                  placeholder="스키마 설명"
                  rows={3}
                />
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  취소
                </Button>
              </DialogActionTrigger>
              <Button variant="solid" type="submit" loading={isSubmitting}>
                수정
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default EditSchema;
